import { LoadingUserPreferences } from 'bootstrapper/LoadingUserPreferences';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ScheduledQuotesBlotterRoute } from 'routes/Blotters/ScheduledQuotesBlotterRoute';
import { EssentialWorkspaceRoute } from 'routes/essential-workspace';
import { SecondaryMarketBatchWorkspaceRoute } from 'routes/secondary-market-batch';
import { SecondaryTradesBlotterRoute } from 'routes/secondary-market-trade-blotter/SecondaryTradesBlotterRoute';
import { SmartQuoteWorkspaceRoute } from 'routes/smart-quote';
import { type RoutePath } from 'state/router';
import { routeChanged } from 'state/router/router.action';
import * as routerSelectors from 'state/selectors/common/router';
import { AppState } from 'state/state.model';

import { AddonManagementRoute } from './addonManagement/addonManagementRoute';
import { PriceImprovementBlotterRoute } from './Blotters/PriceImprovementBlotterRoute';
import { QuotesBlotterRoute } from './Blotters/QuotesBlotterRoute/quotes-blotter-route.suspense';
import { TradesBlotterRoute } from './Blotters/TradesBlotterRoute';
import { LoadQuoteRoute } from './LoadQuoteRoute';
import { LoadRfqRoute } from './LoadRfqRoute';
import { SecondaryOrdersBlotterRoute } from './secondaryMarketOrderBlotter/SecondaryOrdersBlotterRoute';
import { UnderlyingUniverseRoute } from './UnderlyingUniverseRoute';
import { WelcomePageRoute } from './welcomePage/WelcomePageRoute';
import { BatchWorkspaceRoute } from './Workspaces/BatchWorkspaceRoute';
import { DynamicWorkspaceRoute } from './Workspaces/DynamicWorkspaceRoute';
import { PostTradeWorkspaceRoute } from './Workspaces/PostTradeWorkSpaceRoute';
import { SecondaryMarketPostEventWorkspaceRoute } from './Workspaces/SecondaryMarketPostEventWorkspace';
import { SecondaryMarketQuotesWorkspaceRoute } from './Workspaces/SecondaryMarketQuotesWorkspace';
import { StaticWorkspaceRoute } from './Workspaces/StaticWorkspaceRoute';

export function AppRouter() {
  const location = useLocation();
  const dispatch = useDispatch();

  const handleRouteChanged = useCallback(() => {
    dispatch(routeChanged(location.pathname as RoutePath));
  }, [dispatch, location.pathname]);

  useEffect(() => {
    handleRouteChanged();
  }, [handleRouteChanged]);

  const defaultRoutePath = useSelector((state: AppState) => routerSelectors.getDefaultRoutePath(state));
  const isWelcomePageVisible = useSelector((state: AppState) => routerSelectors.isDiscoverPageVisible(state));
  const isBatchWorkspaceEnabled = useSelector((state: AppState) => routerSelectors.isBatchWorkspaceVisible(state));
  const isAdvancedWorkspaceVisible = useSelector((state: AppState) =>
    routerSelectors.isAdvancedWorkspaceVisible(state),
  );
  const isEssentialWorkspaceVisible = useSelector((state: AppState) =>
    routerSelectors.isEssentialWorkspaceVisible(state),
  );
  const isSmartQuoteWorkspaceVisible = useSelector((state: AppState) =>
    routerSelectors.isSmartQuoteWorkspaceVisible(state),
  );
  const isDynamicWorkspaceVisible = useSelector((state: AppState) => routerSelectors.isDynamicWorkspaceVisible(state));
  const isPostTradeVisible = useSelector((state: AppState) => routerSelectors.isPostTradeVisible(state));
  const isQuoteBlotterVisible = useSelector((state: AppState) => routerSelectors.isQuoteBlotterVisible(state));
  const isTradeBlotterVisible = useSelector((state: AppState) => routerSelectors.isTradeBlotterVisible(state));
  const isAddonManagementVisible = useSelector((state: AppState) => routerSelectors.isAddonManagementVisible(state));
  const isPriceImprovementBlotterVisible = useSelector((state: AppState) =>
    routerSelectors.isPriceImprovementBlotterVisible(state),
  );
  const isSecondaryQuoteEnabled = useSelector((state: AppState) => routerSelectors.isSecondaryQuoteEnabled(state));
  const isSecondaryMarketTradeBlotterVisible = useSelector((state: AppState) =>
    routerSelectors.isSecondaryTradeBlotterVisible(state),
  );
  const isSecondaryOrderBlotterVisible = useSelector((state: AppState) =>
    routerSelectors.isSecondaryOrderBlotterVisible(state),
  );
  const isSecondaryPostTradeVisible = useSelector((state: AppState) =>
    routerSelectors.isSecondaryPostEventVisible(state),
  );
  const isSecondaryBatchVisible = useSelector((state: AppState) => routerSelectors.isSecondaryBatchVisible(state));
  const isScheduledQuotesBlotterVisible = useSelector((state: AppState) =>
    routerSelectors.isScheduledQuotesBlotterVisible(state),
  );

  return (
    <Routes>
      {isWelcomePageVisible && <Route path="/home" element={<WelcomePageRoute />} />}
      {isSmartQuoteWorkspaceVisible && <Route path="/smart-quote" element={<SmartQuoteWorkspaceRoute />} />}
      {isEssentialWorkspaceVisible && <Route path="/essential" element={<EssentialWorkspaceRoute />} />}
      {isAdvancedWorkspaceVisible && <Route path="/static" element={<StaticWorkspaceRoute />} />}
      {isSecondaryQuoteEnabled && (
        <>
          <Route path="/secondary-market" element={<Navigate to="/secondary-market-quotes-workspace" />} />
          <Route path="/secondary-market-quotes-workspace" element={<SecondaryMarketQuotesWorkspaceRoute />} />
          {isSecondaryBatchVisible && (
            <>
              <Route path="/secondary-market-batch" element={<SecondaryMarketBatchWorkspaceRoute />} />
              <Route path="/secondary-market-batch/:orderType" element={<SecondaryMarketBatchWorkspaceRoute />} />
            </>
          )}
          {isSecondaryPostTradeVisible && (
            <Route path="/secondary-market-post-event-workspace" element={<SecondaryMarketPostEventWorkspaceRoute />} />
          )}
        </>
      )}
      {isDynamicWorkspaceVisible && <Route path="/dynamic" element={<DynamicWorkspaceRoute />} />}
      {isBatchWorkspaceEnabled && <Route path="/batch" element={<BatchWorkspaceRoute />} />}
      {isPostTradeVisible && <Route path="/post-trade" element={<PostTradeWorkspaceRoute />} />}
      {isQuoteBlotterVisible && <Route path="/quote-blotter" element={<QuotesBlotterRoute />} />}
      {isTradeBlotterVisible && <Route path="/trade-blotter" element={<TradesBlotterRoute />} />}
      {isPriceImprovementBlotterVisible && (
        <Route path="/price-improvement-blotter" element={<PriceImprovementBlotterRoute />} />
      )}
      {isScheduledQuotesBlotterVisible && (
        <Route path="/scheduled-quotes-blotter" element={<ScheduledQuotesBlotterRoute />} />
      )}
      {isAddonManagementVisible && <Route path="/addon-management" element={<AddonManagementRoute />} />}
      {isSecondaryMarketTradeBlotterVisible && (
        <Route path="/secondary-trade-blotter" element={<SecondaryTradesBlotterRoute />} />
      )}
      {isSecondaryOrderBlotterVisible && (
        <Route path="/secondary-orders-blotter" element={<SecondaryOrdersBlotterRoute />} />
      )}
      <Route path="/underlying-universe" element={<UnderlyingUniverseRoute />} />
      <Route path="/quote/:externalId" element={<LoadQuoteRoute />} />
      <Route path="/sharable-quote/:rfqId" element={<LoadRfqRoute />} />
      <Route path="/loading-user-preferences" element={<LoadingUserPreferences />} />
      <Route path="/" element={<Navigate to={defaultRoutePath} />} />
      <Route path="*" element={<Navigate to={defaultRoutePath} />} />
    </Routes>
  );
}
